import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Check, ExternalLink, ShieldCheck, Loader2, Copy, AlertTriangle, Coins } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TelegramMiniApp = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [claimedCode, setClaimedCode] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [initData, setInitData] = useState("");
  const [error, setError] = useState(null);
  const [tokens, setTokens] = useState(0);
  const [canClaimToday, setCanClaimToday] = useState(false);
  const [isClaimingTokens, setIsClaimingTokens] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const tg = window.Telegram?.WebApp;
        if (!tg) {
          setError("Bu sayfa sadece Telegram Mini App olarak acilabilir.");
          setIsLoading(false);
          return;
        }

        tg.ready();
        tg.expand();

        const themeParams = tg.themeParams || {};
        document.documentElement.style.setProperty("--tg-bg", themeParams.bg_color || "#0a0a0a");
        document.documentElement.style.setProperty("--tg-text", themeParams.text_color || "#ffffff");

        const rawInitData = tg.initData;
        if (!rawInitData) {
          setError("Telegram dogrulama verisi bulunamadi.");
          setIsLoading(false);
          return;
        }

        setInitData(rawInitData);

        const res = await axios.post(`${API}/telegram/validate-user`, {
          init_data: rawInitData,
        });

        if (res.data.valid) {
          setIsValid(true);
          setUser(res.data.user);

          // Fetch campaigns and user info in parallel
          const [campRes, userInfoRes] = await Promise.all([
            axios.post(`${API}/telegram/campaigns`, { init_data: rawInitData }),
            axios.post(`${API}/telegram/user-info`, { init_data: rawInitData })
          ]);
          setCampaigns(campRes.data.campaigns || []);
          setTokens(userInfoRes.data.tokens || 0);
          setCanClaimToday(userInfoRes.data.can_claim_today);
        } else {
          setError(res.data.message);
        }
      } catch (e) {
        setError("Baglanti hatasi. Lutfen tekrar deneyin.");
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const claimDailyTokens = useCallback(async () => {
    if (isClaimingTokens || !canClaimToday) return;
    setIsClaimingTokens(true);
    try {
      const res = await axios.post(`${API}/telegram/claim-daily-tokens`, {
        init_data: initData,
      });
      if (res.data.success) {
        setTokens(res.data.total_tokens);
        setCanClaimToday(false);
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
        setCanClaimToday(false);
      }
    } catch (e) {
      toast.error("Jeton alinamadi.");
    } finally {
      setIsClaimingTokens(false);
    }
  }, [initData, isClaimingTokens, canClaimToday]);

  const claimCode = useCallback(
    async (campaignId) => {
      if (claimingId) return;
      setClaimingId(campaignId);

      try {
        const res = await axios.post(`${API}/telegram/claim-code`, {
          init_data: initData,
          campaign_id: campaignId,
        });

        if (res.data.success) {
          setClaimedCode(res.data);
          setCampaigns((prev) =>
            prev.map((c) =>
              c.id === campaignId ? { ...c, already_claimed: true } : c
            )
          );
          toast.success("Kod basariyla alindi!");
        } else {
          toast.error(res.data.message);
        }
      } catch (e) {
        toast.error("Kod alinamadi.");
      } finally {
        setClaimingId(null);
      }
    },
    [initData, claimingId]
  );

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Kod kopyalandi!");
  };

  if (isLoading) {
    return (
      <div className="miniapp-container" data-testid="miniapp-loading">
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <Loader2 className="w-10 h-10 text-[#CCFF00] animate-spin" />
          <p className="text-[#A1A1AA] text-sm font-mono">Dogrulanıyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="miniapp-container" data-testid="miniapp-error">
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6">
          <AlertTriangle className="w-12 h-12 text-red-500" />
          <p className="text-white text-center font-medium">{error}</p>
          <p className="text-[#A1A1AA] text-sm text-center">
            Bu sayfayi Telegram uzerinden acin:
          </p>
          <a
            href="https://t.me/PromocodeAI_bot/PROM"
            className="text-[#0088cc] underline text-sm"
            data-testid="miniapp-telegram-link"
          >
            t.me/PromocodeAI_bot/PROM
          </a>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (claimedCode) {
    return (
      <div className="miniapp-container" data-testid="miniapp-claimed">
        <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-[#CCFF00]/20 rounded-full flex items-center justify-center"
          >
            <Check className="w-10 h-10 text-[#CCFF00]" />
          </motion.div>

          <div className="text-center">
            <p className="text-[#A1A1AA] text-sm mb-1">{claimedCode.campaign_name}</p>
            <h2 className="text-white text-lg font-bold mb-4">Kodunuz Hazir!</h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm bg-[#1a1a2e] border-2 border-[#CCFF00]/50 rounded-xl p-6 text-center"
            data-testid="miniapp-code-display"
          >
            <p className="font-mono text-3xl font-bold text-[#CCFF00] tracking-widest mb-4">
              {claimedCode.code}
            </p>
            <Button
              onClick={() => copyCode(claimedCode.code)}
              className="w-full bg-[#CCFF00] hover:bg-[#b8e600] text-black font-bold"
              data-testid="miniapp-copy-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Kodu Kopyala
            </Button>
          </motion.div>

          {claimedCode.redirect_url && (
            <a
              href={claimedCode.redirect_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#00FFFF] text-sm hover:underline"
              data-testid="miniapp-redirect-link"
            >
              <ExternalLink className="w-4 h-4" />
              Kodu Kullanmak Icin Tikla
            </a>
          )}

          <Button
            onClick={() => setClaimedCode(null)}
            variant="ghost"
            className="text-[#A1A1AA] hover:text-white"
            data-testid="miniapp-back-btn"
          >
            Kampanyalara Don
          </Button>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="miniapp-container" data-testid="miniapp-main">
      <div className="min-h-screen px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#CCFF00] to-[#00FFFF] rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-black" />
            </div>
            <h1 className="text-white text-xl font-bold tracking-tight">PROMO CODE</h1>
          </div>
          {user && (
            <p className="text-[#A1A1AA] text-sm" data-testid="miniapp-welcome">
              Hos geldin, <span className="text-[#CCFF00]">{user.first_name}</span>!
            </p>
          )}
          <div className="flex items-center justify-center gap-2 mt-2">
            <ShieldCheck className="w-3 h-3 text-green-500" />
            <span className="text-green-500 text-xs font-mono">Telegram Dogrulanmis</span>
          </div>
        </div>

        {/* Token Section */}
        <div className="max-w-md mx-auto mb-6" data-testid="miniapp-token-section">
          <div className="bg-gradient-to-r from-[#CCFF00]/10 to-[#00FFFF]/10 border border-[#CCFF00]/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-[#CCFF00]" />
                <span className="text-white font-bold text-sm">Jetonlarim</span>
              </div>
              <span className="text-[#CCFF00] font-mono text-2xl font-bold" data-testid="miniapp-token-balance">
                {tokens}
              </span>
            </div>
            <Button
              onClick={claimDailyTokens}
              disabled={!canClaimToday || isClaimingTokens}
              className={`w-full font-bold text-sm ${
                canClaimToday
                  ? "bg-[#CCFF00] hover:bg-[#b8e600] text-black"
                  : "bg-[#2A2A2A] text-[#A1A1AA] cursor-not-allowed"
              }`}
              data-testid="miniapp-claim-tokens-btn"
            >
              {isClaimingTokens ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Coins className="w-4 h-4 mr-2" />
              )}
              {canClaimToday ? "Gunluk Jeton Al" : "Yarin Tekrar Gel!"}
            </Button>
          </div>
        </div>

        {/* Campaigns */}
        <div className="space-y-3 max-w-md mx-auto">
          <p className="text-[#A1A1AA] text-xs font-mono uppercase tracking-wider mb-4 text-center">
            Aktif Kampanyalar ({campaigns.filter(c => !c.already_claimed).length})
          </p>

          {campaigns.length === 0 && (
            <div className="text-center py-12" data-testid="miniapp-no-campaigns">
              <Gift className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA]/30" />
              <p className="text-[#A1A1AA]">Su an aktif kampanya yok.</p>
              <p className="text-[#A1A1AA]/60 text-sm mt-1">
                Yeni kampanyalar icin takipte kalin!
              </p>
            </div>
          )}

          <AnimatePresence>
            {campaigns.map((camp, idx) => (
              <motion.div
                key={camp.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                className={`rounded-xl p-4 border transition-all ${
                  camp.already_claimed
                    ? "bg-[#1a1a2e]/50 border-[#2a2a3e]"
                    : "bg-[#1a1a2e] border-[#CCFF00]/20 hover:border-[#CCFF00]/50"
                }`}
                data-testid={`miniapp-campaign-${camp.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-sm truncate ${camp.already_claimed ? "text-[#A1A1AA]/50" : "text-white"}`}>
                      {camp.name}
                    </h3>
                    {camp.description && (
                      <p className="text-[#A1A1AA] text-xs mt-1 truncate">
                        {camp.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    {camp.already_claimed ? (
                      <div className="flex items-center gap-1 text-green-500 text-xs" data-testid={`miniapp-claimed-badge-${camp.id}`}>
                        <Check className="w-4 h-4" />
                        <span>Alindi</span>
                      </div>
                    ) : (
                      <Button
                        onClick={() => claimCode(camp.id)}
                        disabled={claimingId === camp.id}
                        size="sm"
                        className="bg-[#CCFF00] hover:bg-[#b8e600] text-black font-bold text-xs"
                        data-testid={`miniapp-claim-btn-${camp.id}`}
                      >
                        {claimingId === camp.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          "Kod Al"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
};

export default TelegramMiniApp;
